import React, { useEffect, useRef } from 'react'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import Close from '@mui/icons-material/Close'
import PushPin from '@mui/icons-material/PushPin'
import PushPinOutlined from '@mui/icons-material/PushPinOutlined'
import { TOUCH_MIN } from 'core/theme.tokens'
import { AssistantMessageExtras, messageHasWork } from './AssistantMessageExtras'
import { DemoPreviewSample } from './AssistantPreviewCard'
import type {
  AssistantChartData,
  AssistantChartType,
  AssistantChatMessage,
  ProposedAction,
} from './useAssistantChat'

export type StageChart = {
  id: string
  chart: AssistantChartData
  pinned: boolean
}

export type StageEntry = {
  message: AssistantChatMessage
  prompt: string
  pinned: boolean
}

export const WORKSPACE_STARTERS: Array<{ group: string; prompts: string[] }> = [
  { group: 'Gray Book', prompts: ['Gray Book price for stock…', 'Compare deals across stock numbers'] },
  { group: 'Inventory', prompts: ['Find truck by stock number', 'Which shop should I send stock…'] },
  { group: 'Sales', prompts: ['Top sales this month', 'Pending sales sitting too long'] },
  { group: 'Operations', prompts: ['Spend by category', 'Late and extra hours this week'] },
]

interface AssistantWorkStageProps {
  pinned: StageEntry[]
  history: StageEntry[]
  tallCharts: boolean
  streaming: boolean
  dismissedActions: Set<string>
  busyActionKey: string | null
  actionKey: (messageId: string, action: ProposedAction, index: number) => string
  onConfirm: (action: ProposedAction, messageId: string, index: number) => void
  onDismiss: (key: string) => void
  onPickChartType: (type: AssistantChartType) => void
  onOpenPath: (path: string) => void
  onSuggest: (prompt: string) => void
  onTogglePin: (messageId: string) => void
  onRemove: (messageId: string) => void
  onClear: () => void
  onHide: () => void
  showDemoPreview?: boolean
  isChartPinned: (messageId: string, index: number) => boolean
  onToggleChartPin: (messageId: string, index: number) => void
}

function resultCount(count: number): string {
  const formatted = count.toLocaleString('en-US')
  return count === 1 ? `${formatted} result` : `${formatted} results`
}

const StageBlock: React.FC<{
  entry: StageEntry
  tallCharts: boolean
  streaming: boolean
  dismissedActions: Set<string>
  busyActionKey: string | null
  actionKey: (messageId: string, action: ProposedAction, index: number) => string
  onConfirm: (action: ProposedAction, messageId: string, index: number) => void
  onDismiss: (key: string) => void
  onPickChartType: (type: AssistantChartType) => void
  onOpenPath: (path: string) => void
  onSuggest: (prompt: string) => void
  onTogglePin: (messageId: string) => void
  onRemove: (messageId: string) => void
  isChartPinned: (messageId: string, index: number) => boolean
  onToggleChartPin: (messageId: string, index: number) => void
}> = ({
  entry,
  tallCharts,
  streaming,
  dismissedActions,
  busyActionKey,
  actionKey,
  onConfirm,
  onDismiss,
  onPickChartType,
  onOpenPath,
  onSuggest,
  onTogglePin,
  onRemove,
  isChartPinned,
  onToggleChartPin,
}) => (
  <Box
    sx={{
      mb: 1.5,
      p: { xs: 1.25, sm: 1.5 },
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: entry.pinned ? 'primary.light' : 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction='row' spacing={0.5} sx={{ alignItems: 'flex-start', mb: 0.5 }}>
      <Typography
        variant='body2'
        title={entry.prompt || 'Result'}
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 600,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {entry.prompt || 'Result'}
      </Typography>
      <Tooltip title={entry.pinned ? 'Unpin' : 'Pin'}>
        <IconButton
          size='small'
          aria-label={entry.pinned ? 'Unpin result' : 'Pin result'}
          aria-pressed={entry.pinned}
          color={entry.pinned ? 'primary' : 'default'}
          onClick={() => onTogglePin(entry.message.id)}
          sx={{ width: 40, height: 40, flexShrink: 0 }}
        >
          {entry.pinned ? <PushPin fontSize='small' /> : <PushPinOutlined fontSize='small' />}
        </IconButton>
      </Tooltip>
      <Tooltip title='Remove from work side'>
        <IconButton
          size='small'
          aria-label='Remove from work side'
          onClick={() => onRemove(entry.message.id)}
          sx={{ width: 40, height: 40, flexShrink: 0 }}
        >
          <Close fontSize='small' />
        </IconButton>
      </Tooltip>
    </Stack>
    <AssistantMessageExtras
      message={entry.message}
      dense={false}
      tallCharts={tallCharts}
      dismissedActions={dismissedActions}
      busyActionKey={busyActionKey}
      actionKey={actionKey}
      onConfirm={onConfirm}
      onDismiss={onDismiss}
      onPickChartType={onPickChartType}
      onOpenPath={onOpenPath}
      onSuggest={onSuggest}
      suggestDisabled={streaming}
      isChartPinned={index => isChartPinned(entry.message.id, index)}
      onToggleChartPin={index => onToggleChartPin(entry.message.id, index)}
      actions='exceptRoutine'
    />
  </Box>
)

export const AssistantWorkStage: React.FC<AssistantWorkStageProps> = ({
  pinned,
  history,
  tallCharts,
  streaming,
  dismissedActions,
  busyActionKey,
  actionKey,
  onConfirm,
  onDismiss,
  onPickChartType,
  onOpenPath,
  onSuggest,
  onTogglePin,
  onRemove,
  onClear,
  onHide,
  showDemoPreview = false,
  isChartPinned,
  onToggleChartPin,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const newest = history[history.length - 1]?.message
  const seenNewestId = useRef('')
  const total = pinned.length + history.length
  const blockProps = {
    tallCharts,
    streaming,
    dismissedActions,
    busyActionKey,
    actionKey,
    onConfirm,
    onDismiss,
    onPickChartType,
    onOpenPath,
    onSuggest,
    onTogglePin,
    onRemove,
    isChartPinned,
    onToggleChartPin,
  }

  useEffect(() => {
    const node = scrollRef.current
    if (!node || !newest) return
    const isNew = seenNewestId.current !== newest.id
    seenNewestId.current = newest.id
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight
    if (!isNew && distanceFromBottom > 80) return
    const scroll = () => node.scrollTo({ top: node.scrollHeight })
    scroll()
    const frame = window.requestAnimationFrame(scroll)
    return () => window.cancelAnimationFrame(frame)
  }, [newest])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', flex: 1 }}>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'center',
          px: { xs: 1.25, sm: 2 },
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          Work
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {total > 0 ? resultCount(total) : 'Results stay here'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size='small'
          color='inherit'
          disabled={history.length === 0}
          onClick={onClear}
          sx={{ minHeight: 36, textTransform: 'none' }}
        >
          Clear
        </Button>
        <Button
          size='small'
          color='inherit'
          onClick={onHide}
          sx={{ minHeight: 36, textTransform: 'none' }}
        >
          Hide
        </Button>
      </Stack>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 1.25, sm: 2 },
          py: 1.5,
        }}
      >
        {showDemoPreview ? <DemoPreviewSample /> : null}
        {total === 0 ? (
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
              Ask for a list, a chart, or a truck. Tables, previews, and files stack here. Pin one to
              keep it, then clear the rest.
            </Typography>
            <Stack spacing={1.5}>
              {WORKSPACE_STARTERS.map(group => (
                <Box key={group.group}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                    {group.group}
                  </Typography>
                  <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 0.5 }}>
                    {group.prompts.map(prompt => (
                      <Button
                        key={prompt}
                        size='small'
                        variant='outlined'
                        disabled={streaming}
                        onClick={() => onSuggest(prompt)}
                        sx={{ minHeight: TOUCH_MIN, textTransform: 'none' }}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          <>
            {pinned.length > 0 ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                  Pinned
                </Typography>
                <Stack sx={{ mt: 0.75 }}>
                  {pinned.map(entry => (
                    <StageBlock key={entry.message.id} entry={entry} {...blockProps} />
                  ))}
                </Stack>
              </Box>
            ) : null}
            {history.length > 0 ? (
              <Box>
                {pinned.length > 0 ? (
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                    History
                  </Typography>
                ) : null}
                <Stack sx={{ mt: pinned.length > 0 ? 0.75 : 0 }}>
                  {history.map(entry => (
                    <StageBlock key={entry.message.id} entry={entry} {...blockProps} />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  )
}

export function promptBefore(messages: AssistantChatMessage[], index: number): string {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const message = messages[cursor]
    if (message?.role === 'user' && message.content.trim()) return message.content.trim()
  }
  return ''
}

export function messageOnStage(message: AssistantChatMessage): boolean {
  return message.role === 'assistant' && messageHasWork(message)
}
